/* eslint-disable jsx-a11y/anchor-is-valid */
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Model, PanelModel } from 'survey-core';
import { Survey } from 'survey-react-ui';
import parse from 'html-react-parser';
import debounce from 'lodash/debounce';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import languages from '../Data/languages';
import Service from '../Utilities/Service';
import SurveyType, { SurveyTypeValue } from '../Data/SurveyType';
import Theme from '../Data/Theme';
import SurveyInaccessibleType, { InaccessibleTypeValue } from '../Data/SurveyInaccessibleType';
import type { StatusValue } from '../Data/Status';
import {
  updateResponse,
  createPopupResponse,
  createAnonymousResponse,
  showSurveyInaccessiblePage,
  setCssClasses,
  animate,
  getModifiedSurveySchema,
  getSurveyAnswers
} from './Helper';
import './index.css';
import { SurveyHeader } from './SurveyHeader';
// eslint-disable-next-line import/extensions
import 'survey-core/survey.i18n.js';
import { NoInternetIcon } from '../Images/NoInternetIcon';
import handleServiceError, { isResponseLimitReached } from '../Utilities/handleServiceError';
import { FooterContent } from '../components/FooterContent';
import { PopupEventTypes } from '../Data/PopupEventTypes';
import useSignals from '../Utilities/useSignals';
import { useSafeLocalStorage } from '../Utilities/useSafeLocalStorage';
import { createSmileyImage } from 'Utilities/smileyImages';
import type {
  PopupMessage,
  Respondent,
  SurveyAnswer,
  SurveyResponse,
  SurveySchema,
  Template
} from '../types';

interface SurveyComponentProps {
  rating?: number | string;
  respondent: Respondent;
  surveySchema: SurveySchema;
  enablePopup?: boolean;
  instanceId?: string | null;
  hideFooter?: boolean;
  hideHeader?: boolean;
  shouldUseLocalStorage?: boolean;
  surveyType: SurveyTypeValue;
  surveyTheme?: number | null;
  forceRatingPrimaryColor?: boolean;
  forceRatingResponsiveness?: boolean;
  acceptingResponses?: boolean;
  allowEditResponse?: boolean;
  template: Template;
  setStatus: (status: StatusValue) => void;
  setErrorDetails: (details: string) => void;
  enableSmileys?: boolean;
  surveyDoAnimation?: boolean | null;
  reduceSpaceAfterRatingQuestionInPanels?: boolean;
  unsubscribedInitially?: boolean;
  responseLimitReachedInitially?: boolean;
  campaignMemberPublicGuid?: string | null;
  campaign?: string | null;
}

const getBackgroundColor = (): string => {
  const backgroundElement = document.getElementById('survey-page-wrapper');
  if (!backgroundElement) return '';
  return globalThis.getComputedStyle(backgroundElement).backgroundColor;
};

const getInitialRatingFromProps = (rating?: number | string): number | null => {
  if (typeof rating === 'number') {
    return rating;
  }

  if (typeof rating === 'string' && rating.trim() !== '') {
    const parsedRating = Number(rating);
    if (!Number.isNaN(parsedRating)) {
      return parsedRating;
    }
  }

  return null;
};

const resolveTemplate = (respondent: Respondent): Template => {
  const template = respondent.template ?? respondent.campaign?.template;
  if (!template) {
    throw new Error('Survey respondent is missing a template.');
  }
  return template;
};

// Collapses the gap after a rating question that sits inside a panel.
// Extracted to module scope to keep the survey event handlers shallow.
const reduceSpaceAfterRatingInPanels = (survey: Model) => {
  survey.pages.forEach((page) => {
    page.questions.forEach((question) => {
      const isRatingInPanel =
        question.getType() === 'rating' && question.parent.getType() === 'panel';
      if (!isRatingInPanel) return;

      const hasNextQuestionInPanel = page.questions.some(
        (q) => q.parent === question.parent && q !== question
      );
      if (!hasNextQuestionInPanel) {
        return;
      }

      const panel = question.parent as PanelModel;
      const name = panel.elements[1]?.name;
      if (!name) return;

      const rootElement = document.querySelector<HTMLElement>(`[data-name="${name}"]`);
      rootElement?.style.setProperty('margin-top', '-40px', 'important');
    });
  });
};

const service = new Service().proxy;
export const SurveyComponent: FunctionComponent<SurveyComponentProps> = ({
  rating,
  respondent,
  surveySchema,
  enablePopup,
  instanceId,
  hideFooter,
  hideHeader,
  shouldUseLocalStorage,
  surveyType,
  surveyTheme,
  forceRatingPrimaryColor,
  forceRatingResponsiveness,
  acceptingResponses,
  allowEditResponse,
  template: templateProp,
  setStatus,
  setErrorDetails,
  enableSmileys,
  surveyDoAnimation,
  reduceSpaceAfterRatingQuestionInPanels,
  unsubscribedInitially,
  responseLimitReachedInitially,
  campaignMemberPublicGuid,
  campaign
}) => {
  const template = resolveTemplate(respondent);
  const [survey] = useState<Model>(() => {
    const surveyModel = new Model(
      getModifiedSurveySchema({
        schema: surveySchema.json,
        template
      })
    );
    const initialRating = getInitialRatingFromProps(rating);
    if (initialRating !== null) {
      surveyModel.data = {
        ...surveyModel.data,
        rating: initialRating
      };
    }
    return surveyModel;
  });
  const { signalSurveyCompleted, signalReady, signalSurveyPageChanged, attachSurveyContentRef } =
    useSignals({
      enablePopup,
      instanceId,
      getBackgroundColor
    });

  const showFooter = useMemo(() => {
    return !(enablePopup || hideFooter);
  }, [enablePopup, hideFooter]);

  const showHeader = useMemo(() => {
    return !enablePopup && !hideHeader;
  }, [enablePopup, hideHeader]);

  // the template is placed differently, depending on if it is a universal or personal link
  const [responseLocalStorage, setResponseLocalStorage] = useSafeLocalStorage<SurveyAnswer[]>(
    `nps-survey-asm-${campaignMemberPublicGuid ?? campaign}`,
    [],
    shouldUseLocalStorage
  );

  const [stylingReady, setStylingReady] = useState(false);
  const popupDataRef = useRef<PopupMessage | null>(null);

  const appInsights = useAppInsightsContext();
  const footerRef = useRef<HTMLDivElement>(null);
  const responseRef = useRef<SurveyResponse>(respondent.response ?? {});
  const doAnimationRef = useRef(true); // is only used if surveyDoAnimation is true.

  // used for universal link campaigns, after posting the response. If needing to edit it, this ref should hold the new id
  const newAnonymousPublicGuid = useRef('');
  const responseLimitReachedRef = useRef(Boolean(responseLimitReachedInitially));

  const showResponseLimitReachedPage = useCallback(() => {
    responseLimitReachedRef.current = true;
    survey.clear(false, false);
    showSurveyInaccessiblePage(survey, SurveyInaccessibleType.RESPONSE_LIMIT_REACHED, template);
  }, [survey, template]);

  // updates response with the newest survey.js answers
  const saveAnswers = useCallback(async () => {
    const closedInformation = await service.responseClosed();
    updateResponse(closedInformation, survey, surveyType, responseRef.current, template);
  }, [surveyType, survey]);

  const debouncedUpdateLocalStorage = useMemo(() => {
    return debounce((surveyInstance: Model) => {
      setResponseLocalStorage(getSurveyAnswers(surveyInstance));
    }, 400);
  }, [setResponseLocalStorage]);

  const saveResponse = useCallback(async () => {
    if (responseLimitReachedRef.current) return;
    try {
      await saveAnswers();
      const res = await service.editResponse(responseRef.current);
      responseRef.current = res;
    } catch (e) {
      if (isResponseLimitReached(e)) {
        showResponseLimitReachedPage();
        return;
      }
      handleServiceError(e, setStatus, setErrorDetails);
    }
  }, [setStatus, saveAnswers, setErrorDetails, showResponseLimitReachedPage]);

  const saveFinalResponse = useCallback(async () => {
    try {
      if (enablePopup) {
        await service.addPopupResponse(
          createPopupResponse(survey, template),
          popupDataRef.current?.campaignMember
        );
        signalSurveyCompleted();
        return;
      }
      if (surveyType === SurveyType.UNIVERSAL) {
        if (newAnonymousPublicGuid.current) {
          await service.updatePublicConsentOnAnonymousCampaignMember(
            responseRef.current,
            newAnonymousPublicGuid.current
          );
        } else {
          responseRef.current = createAnonymousResponse(survey, template);
          newAnonymousPublicGuid.current = await service.addUniversalLinkResponse(
            responseRef.current
          );
        }
        return;
      }

      await saveAnswers();
      await service.addFinalResponse(responseRef.current);
    } catch (e) {
      if (isResponseLimitReached(e)) {
        showResponseLimitReachedPage();
        return;
      }
      handleServiceError(e, setStatus, setErrorDetails);
    }
  }, [
    surveyType,
    setStatus,
    setErrorDetails,
    enablePopup,
    saveAnswers,
    survey,
    signalSurveyCompleted,
    showResponseLimitReachedPage
  ]);

  const updateFinishButtonStyling = useCallback(() => {
    const finishButtonElement = document.getElementsByClassName('sd-navigation__complete-btn')?.[0];
    if (finishButtonElement) {
      const isOnline = navigator.onLine;
      const buttonWrapperClassList = finishButtonElement.parentElement?.classList;
      const buttonClassList = finishButtonElement.classList;
      if (isOnline) {
        buttonWrapperClassList?.remove('disableButtonWrapper');
        buttonClassList?.remove('disableButton');
      } else {
        buttonWrapperClassList?.add('disableButtonWrapper');
        buttonClassList?.add('disableButton');
      }

      const icon = document.getElementById('noInternetIcon');
      if (icon) {
        finishButtonElement.after(icon);
      }
    }
  }, []);

  useEffect(() => {
    globalThis.addEventListener('online', updateFinishButtonStyling);
    globalThis.addEventListener('offline', updateFinishButtonStyling);

    return () => {
      globalThis.removeEventListener('online', updateFinishButtonStyling);
      globalThis.removeEventListener('offline', updateFinishButtonStyling);
    };
  }, [updateFinishButtonStyling]);

  useEffect(() => {
    const primaryColor = template?.colorScheme;
    if (primaryColor) {
      document.documentElement.style.setProperty('--primary', primaryColor);
    }

    async function fixFlickeringAndApplyThemeCssFiles() {
      const theme = surveyTheme;
      if (!theme || theme === Theme.defaultV2) {
        await import('survey-core/defaultV2.css');
      } else {
        switch (theme) {
          case Theme.bootstrap:
            await import('bootstrap/dist/css/bootstrap.css');
            await import('survey-core/survey.css');
            break;
          case Theme.modern:
            await import('survey-core/modern.css');
            break;
          default:
            await import('survey-core/survey.css');
        }
      }
      setStylingReady(true);
    }

    async function applyPrimaryColorCss() {
      if (forceRatingPrimaryColor) {
        await import('./rating-primary-color.css');
      }
      setStylingReady(true);
    }

    async function applyRatingResponsivenessCss() {
      if (forceRatingResponsiveness) {
        await import('./rating-one-line.css');
      }
      setStylingReady(true);
    }

    fixFlickeringAndApplyThemeCssFiles();
    applyPrimaryColorCss();
    applyRatingResponsivenessCss();

    const existingSurveyAnswers = responseLocalStorage ?? respondent?.response?.surveyAnswers;
    if (existingSurveyAnswers) {
      existingSurveyAnswers.forEach((surveyAnswer) => {
        const name = surveyAnswer.name;
        const { value } = surveyAnswer;
        const hasNameAndValue = name && value;
        if (hasNameAndValue) {
          survey.setValue(name, value);
        }
      });
    }
    const shouldUpdateSurveyFormWithNewRatingFromProps =
      typeof rating === 'number' && respondent?.response;
    if (shouldUpdateSurveyFormWithNewRatingFromProps) {
      const newRating = rating;
      const currentRating = survey.getValue('rating');

      survey.setValue('rating', rating);

      const newRatingIsDifferentFromCurrentRating = newRating !== currentRating;
      const partialResponseIsAvailable = allowEditResponse && !templateProp.forceSingleSubmit;
      if (newRatingIsDifferentFromCurrentRating && partialResponseIsAvailable) {
        const updatedResponse = { ...responseRef.current, rating: newRating };
        responseRef.current = updatedResponse;
        saveResponse();
      }
    }
    const hideAnonymousQuestion = surveyType === SurveyType.UNIVERSAL;
    if (hideAnonymousQuestion) {
      const anonymous = survey.getQuestionByName('anonymous');
      if (anonymous) {
        anonymous.visible = false;
      }
    }
    if (template.consentForPublicUse) {
      survey.pages.forEach((element) => {
        element.questions.forEach((q) => {
          if (q.name === 'consent') {
            q.visible = true;
            if (template.consentIsOptOut) q.value = true;
          }
        });
      });
    }
  }, [
    surveyTheme,
    forceRatingPrimaryColor,
    forceRatingResponsiveness,
    surveyType,
    rating,
    acceptingResponses,
    template.consentForPublicUse,
    template.consentIsOptOut,
    template?.colorScheme,
    respondent,
    survey,
    allowEditResponse,
    saveResponse,
    templateProp.forceSingleSubmit
  ]);

  useEffect(() => {
    appInsights.trackPageView({ name: 'SurveyComponent.tsx' });
  }, [appInsights]);

  useEffect(() => {
    survey.focusFirstQuestionAutomatic = false;

    survey.onAfterRenderPage.add(() => {
      updateFinishButtonStyling();
    });

    survey.onCurrentPageChanged.add(async (sender: Model) => {
      if (surveyDoAnimation) animate('fadeIn', 1000);
      if (surveyType === SurveyType.UNIVERSAL || template.forceSingleSubmit || !allowEditResponse)
        return;
      await saveResponse();
      appInsights.trackEvent({
        name: 'Changed Page',
        properties: { ...responseRef.current, pageNo: sender.currentPageNo }
      });
    });

    survey.onCurrentPageChanging.add((sender: Model, options: any) => {
      if (enablePopup) {
        signalSurveyPageChanged();
        return;
      }
      if (
        !doAnimationRef.current ||
        !surveyDoAnimation ||
        responseRef.current?.closed ||
        !acceptingResponses ||
        responseLimitReachedRef.current
      )
        return;
      options.allowChanging = false;
      setTimeout(() => {
        doAnimationRef.current = false;
        sender.currentPage = options.newCurrentPage;
        doAnimationRef.current = true;
      }, 1000);
      animate('fadeOut', 1000);
    });
    survey.onComplete.add(async (sender: Model) => {
      await saveFinalResponse();
      appInsights.trackEvent({
        name: 'Finished Survey',
        properties: { ...responseRef.current, pageNo: sender.currentPageNo }
      });

      if (templateProp.autoReload) {
        setTimeout(() => {
          globalThis.location.reload();
        }, 2000);
      }
    });

    survey.onValueChanged.add((surveyInstance: Model, options: any) => {
      const isComment = options.question?.getType() === 'comment';

      if (isComment) {
        // Debounced: runs only after user stops typing
        debouncedUpdateLocalStorage(surveyInstance);
      } else {
        // Immediate for all non-comment questions
        setResponseLocalStorage(getSurveyAnswers(surveyInstance));
      }
    });

    survey.onAfterRenderQuestion.add((sender: Model, options: any) => {
      if (options.question.getType() === 'comment') {
        const inputElement = options.htmlElement.querySelector('textarea');

        inputElement.addEventListener('input', (event: Event) => {
          const newValue = (event.target as HTMLTextAreaElement).value;
          const questionName = options.question.name;
          sender.setValue(questionName, newValue);
        });
      }

      if (options.question.getType() === 'rating' && enableSmileys) {
        const lessLikely = options.htmlElement.querySelector(
          '.sd-rating__item-text.sd-rating__min-text'
        );

        if (lessLikely && lessLikely.firstChild.tagName !== 'IMG') {
          lessLikely.prepend(createSmileyImage('sad'));
          lessLikely.style.display = 'grid';
          lessLikely.style.bottom = '-17px';
          lessLikely.style.fontSize = '11px';
        }

        const moreLikely = options.htmlElement.querySelector(
          '.sd-rating__item-text.sd-rating__max-text'
        );

        if (moreLikely && moreLikely.firstChild.tagName !== 'IMG') {
          moreLikely.prepend(createSmileyImage('happy'));
          moreLikely.style.display = 'grid';
          moreLikely.style.bottom = '-17px';
          moreLikely.style.justifyItems = 'end';
          moreLikely.style.fontSize = '11px';
        }
      }
      if (reduceSpaceAfterRatingQuestionInPanels) {
        reduceSpaceAfterRatingInPanels(survey);
      }
    });

    survey.onUpdateQuestionCssClasses.add((sender: Model, options: any) => {
      setCssClasses(options);
    });

    return () => {
      survey.onComplete.clear();
      survey.onAfterRenderPage.clear();
      survey.onCurrentPageChanged.clear();
      survey.onCurrentPageChanging.clear();
      survey.onValueChanged.clear();
      survey.onAfterRenderQuestion.clear();
      survey.onUpdateQuestionCssClasses.clear();
    };
  }, [
    survey,
    surveyDoAnimation,
    surveyType,
    allowEditResponse,
    enablePopup,
    acceptingResponses,
    enableSmileys,
    reduceSpaceAfterRatingQuestionInPanels,
    templateProp,
    template,
    appInsights,
    updateFinishButtonStyling,
    saveFinalResponse,
    saveResponse,
    signalSurveyPageChanged
  ]);

  useEffect(() => {
    if (!stylingReady || !enablePopup || responseLimitReachedInitially) {
      return undefined;
    }
    const receiveData = (event: MessageEvent) => {
      if (
        event.data.eventType === PopupEventTypes.SET_SURVEY_PARAMETERS &&
        event.data.instanceId === instanceId
      ) {
        popupDataRef.current = event.data;
      }
    };
    globalThis.addEventListener('message', receiveData, false);

    signalReady();
    return () => {
      globalThis.removeEventListener('message', receiveData);
    };
  }, [instanceId, enablePopup, stylingReady, signalReady, responseLimitReachedInitially]);

  const language = template.language.name;

  const fallbackFontFamilies = 'Arial, Helvetica, Work Sans';
  const fontFamily = template.fontFamily
    ? `${template.fontFamily}, ${fallbackFontFamilies}`
    : fallbackFontFamilies;

  const organizationName =
    template?.sentByText || respondent.organizationName || respondent.companyName;

  let inaccessibleType: InaccessibleTypeValue | null = null;
  if (unsubscribedInitially) {
    inaccessibleType = SurveyInaccessibleType.UNSUBSCRIBED;
  } else if (responseRef.current?.closed) {
    inaccessibleType = SurveyInaccessibleType.ALREADY_ANSWERED;
  } else if (!acceptingResponses) {
    inaccessibleType = SurveyInaccessibleType.CAMPAIGN_CLOSED;
  } else if (responseLimitReachedInitially) {
    inaccessibleType = SurveyInaccessibleType.RESPONSE_LIMIT_REACHED;
  }

  useEffect(() => {
    if (inaccessibleType != null && survey) {
      showSurveyInaccessiblePage(survey, inaccessibleType, template);
    }
  }, [inaccessibleType, survey]);

  return (
    <div>
      {stylingReady && (
        <div id="survey-page-wrapper" className="pageWrapper" style={{ fontFamily }}>
          <div className="surveyWrapper" ref={attachSurveyContentRef}>
            {showHeader && <SurveyHeader template={template} />}
            <Survey model={survey} key={survey.locale} />
            {showFooter && (
              <div
                className="info-text"
                ref={footerRef}
                style={{
                  color: '#a0a0a0',
                  fontSize: '13px',
                  textAlign: 'center',
                  margin: '10px 0 10px 0'
                }}
              >
                <FooterContent htmlString={templateProp.footerContent} />
                &nbsp;
                <span>
                  {organizationName ? (
                    <>
                      {parse(languages.RUN_BY[language])}&nbsp;
                      <span>{organizationName}.</span>
                    </>
                  ) : (
                    <>{parse(languages.RUN_BY_COMPANY[language])}.</>
                  )}
                </span>
                {surveyType !== SurveyType.UNIVERSAL && template.enableUnsubscribe && (
                  <>
                    &nbsp;
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const url = new URL(globalThis.location.href);
                        url.searchParams.set('unsubscribe', 'true');
                        globalThis.location.href = url.toString();
                      }}
                      style={{ color: '#b5b5b5' }}
                    >
                      <span>{languages.UNSUBSCRIBE[language]}</span>
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Element below are hidden, used for injection in other components */}
      <NoInternetIcon />
    </div>
  );
};
